/* eslint-disable no-unused-vars */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _post, _put } from "../../Services/baseApi";
import {
  FORGOT_PASSWORD_URL,
  LOGIN_URL,
  LOGOUT_URL,
  OTP_VERIFICATION_URL,
  REGISTER_URL,
  RESEND_OTP_URL,
  SET_NEW_PASSWORD_URL,
} from "../../Services/endpoints";
import { errorHandler, successToast } from "../../Utils/toast";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

const initialState = {
  isLoading: false,
  isAuthenticated: false,
  user: {},
  forgotPassUserData: {},
};

/** Persist token, refresh token, session id + user profile. */
const normalizeAuthResult = (result) => {
  if (!result) return {};
  const { token, refreshToken, sessionId, user } = result;
  return { token, refreshToken, sessionId, user };
};

const persistAuthTokens = (result) => {
  if (result?.token) {
    localStorage.setItem("token", result.token);
  }
  if (result?.refreshToken) {
    localStorage.setItem("refreshToken", result.refreshToken);
  }
  if (result?.sessionId) {
    localStorage.setItem("sessionId", result.sessionId);
  }
};

const clearAuthSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("sessionId");
  localStorage.clear();
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = {};
      state.isAuthenticated = false;
      clearAuthSession();
    },
    updateUserDetailSync(state, action) {
      if (state.user?.user) {
        state.user.user = { ...state.user.user, ...action.payload };
      }
    },
    setAuthTokens(state, action) {
      const { token, refreshToken, sessionId } = action.payload ?? {};
      if (!state.user) {
        state.user = {};
      }
      if (token) {
        state.user.token = token;
      }
      if (refreshToken) {
        state.user.refreshToken = refreshToken;
      }
      if (sessionId) {
        state.user.sessionId = sessionId;
      }
      state.isAuthenticated = Boolean(state.user?.token);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = normalizeAuthResult(action.payload?.result);
        state.isAuthenticated = Boolean(state.user?.token);
      })
      .addCase(loginThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(forgotPasswordThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(forgotPasswordThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.forgotPassUserData = action.payload;
      })
      .addCase(forgotPasswordThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(forgotPasswordOtpVerifyThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(forgotPasswordOtpVerifyThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        const verifiedOtp = action.meta?.arg?.otp;
        if (verifiedOtp) {
          state.forgotPassUserData = {
            ...state.forgotPassUserData,
            verifiedOtp: String(verifiedOtp),
          };
        }
      })
      .addCase(forgotPasswordOtpVerifyThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(forgotSetNewPasswordThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(forgotSetNewPasswordThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.user = action.payload;
      })
      .addCase(forgotSetNewPasswordThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(resendOtpThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resendOtpThunk.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(resendOtpThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(registerThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(registerThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = normalizeAuthResult(action.payload?.result);
        state.isAuthenticated = Boolean(state.user?.token);
      })
      .addCase(registerThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(logoutThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        clearAuthSession();
        state.isLoading = false;
        state.user = {};
        state.isAuthenticated = false;
      })
      .addCase(logoutThunk.rejected, (state) => {
        clearAuthSession();
        state.isLoading = false;
        state.user = {};
        state.isAuthenticated = false;
      });
  },
});

export const { logout, updateUserDetailSync, setAuthTokens } = authSlice.actions;
// export default authSlice.reducer;

// Define the reducer export with persist data
export default persistReducer(
  {
    key: "auth/persist",
    storage,
    whitelist: ["user", "forgotPassUserData"],
  },
  authSlice.reducer
);

export const loginThunk = createAsyncThunk(
  "authSlice/loginThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _post(LOGIN_URL, {
        // userName: payload.userName,
        emailOrPhone: payload.userName,
        password: payload.password,
      });
      const result = normalizeAuthResult(response?.data?.result);
      persistAuthTokens(result);
      response?.data && successToast(response?.message || "Login Successfully");
      return { ...response?.data, result };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  }
);

export const forgotPasswordThunk = createAsyncThunk(
  "authSlice/forgotPasswordThunk",
  async (payload) => {
    try {
      const response = await _post(FORGOT_PASSWORD_URL, {
        emailOrPhone: payload.email,
      });
      response?.data && successToast(response?.data?.message);
      payload.handleFun();
      return { ...response?.data, email: payload.email };
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const resendOtpThunk = createAsyncThunk(
  "authSlice/resendOtpThunk",
  async (payload) => {
    try {
      const response = await _post(RESEND_OTP_URL, {
        // type: payload?.type,
        emailOrPhone: payload.email,
      });
      response?.data && successToast(response?.data?.message);
      payload.handleFun();
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const forgotPasswordOtpVerifyThunk = createAsyncThunk(
  "authSlice/forgotPasswordOtpVerifyThunk",
  async (payload) => {
    try {
      const response = await _post(OTP_VERIFICATION_URL, {
        emailOrPhone: payload.emailOrPhone,
        type: "RESET_BY_EMAIL",
        otp: String(payload.otp),
      });
      response?.data && successToast(response?.data?.message);
      payload.handleFun();
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const forgotSetNewPasswordThunk = createAsyncThunk(
  "authSlice/forgotSetNewPasswordThunk",
  async (payload) => {
    try {
      const response = await _post(SET_NEW_PASSWORD_URL, {
        emailOrPhone: payload.email,
        newPassword: payload.password,
        otp: String(payload.otp),
      });
      response?.data && successToast(response?.data?.message);
      payload.handleFun();
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const registerThunk = createAsyncThunk(
  "authSlice/registerThunk",
  async (payload) => {
    try {
      const response = await _post(REGISTER_URL, {
        name: payload.fullName,
        emailId: payload.email,
        password: payload.password,
      });
      const result = normalizeAuthResult(response?.data?.result);
      persistAuthTokens(result);
      response?.data && successToast(response?.data?.message);
      return { ...response?.data, result };
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const logoutThunk = createAsyncThunk(
  "authSlice/logoutThunk",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _post(LOGOUT_URL);
      response?.data && successToast(response?.message || "Logged out");
      return response?.data;
    } catch (error) {
      // Clear local session even when the server session is already gone.
      clearAuthSession();
      if (error?.response?.status !== 401) {
        errorHandler(error);
      }
      return rejectWithValue(error);
    }
  }
);
